import { ReactNode } from 'react';
import cn from 'classnames';
import PanelButton from '@creditflowresearch/uilib/PanelButton';
import Flex from '@creditflowresearch/uilib/Flex';
import usePanelNavigation from './usePanelNavigation';
import type { INavItem } from './DropdownPanelButtons';

import styles from './dropdownPanelButtons.module.scss';

interface IDesktopDropdownPanelButtonsProps {
  items: INavItem[],
  getPanelContent: (panelId: string) => ReactNode,
  className?: string,
  delayDuration: number,
  skipDelayDuration: number,
  leaveDelay: number,
}

const DesktopDropdownPanelButtons = ({
  items,
  getPanelContent,
  className,
  delayDuration,
  skipDelayDuration,
  leaveDelay,
}: IDesktopDropdownPanelButtonsProps) => {
  const {
    containerRef,
    isPanelOpen,
    activeItemId,
    isTouchDevice,
    containerProps,
    itemProps,
  } = usePanelNavigation({
    items,
    delayDuration,
    skipDelayDuration,
    leaveDelay,
  });

  const activeItem = items.find((item) => item.id === activeItemId);

  return (
    <nav aria-label="Main navigation">
      <div
        ref={ containerRef }
        className={ cn(styles.navContainer, className) }
        data-panel-control
        { ...containerProps }
      >
        <Flex
          direction="row"
          dataTest="dropdownPanelButtons"
        >
          { items.map((item) => {
            const isItemActive = activeItemId === item.id && isPanelOpen;

            return (
              <div
                key={ item.id }
                className={ styles.buttonItemContainer }
                data-test="dropdownPanelButton"
                data-item-id={ item.id }
                { ...itemProps }
              >
                <PanelButton
                  label={ item.label }
                  badge={ typeof item.badge === 'function' ? item.badge(isItemActive) : item.badge }
                  // On touch the trigger is a disclosure button, not a link: tapping must open the
                  // panel rather than navigate away. `aria-expanded` already describes it as such.
                  href={ isTouchDevice ? undefined : item.href }
                  data-panel-open={ isItemActive }
                  aria-expanded={ isItemActive }
                  aria-controls="panel-menu-viewport"
                  aria-keyshortcuts="ArrowDown Space"
                />
              </div>
            );
          }) }
        </Flex>

        <div
          id="panel-menu-viewport"
          role="region"
          aria-label={ activeItem ? `${ activeItem.label } menu` : 'Navigation menu' }
          aria-hidden={ !isPanelOpen }
          inert={ !isPanelOpen }
          data-state={ isPanelOpen ? 'open' : 'closed' }
          className={ styles.viewport }
        >
          <div className={ styles.viewportContent }>
            { items.map((item) => (
              <div
                key={ item.id }
                data-content-state={ activeItemId === item.id ? 'active' : 'inactive' }
                className={ styles.contentPanel }
              >
                { getPanelContent(item.id) }
              </div>
            )) }
          </div>
        </div>
      </div>
    </nav>
  );
};

export default DesktopDropdownPanelButtons;
